import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Copy,
  Check,
  Eye,
  ExternalLink,
  Loader2,
  Settings,
  Sun,
  Moon,
  FileText,
  Clock,
  CalendarDays,
} from "lucide-react";
import AppLayout from "../components/Layout/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { auth } from "../config/firebase";

const API_BASE_URL = import.meta.env.VITE_API_URL;

export default function WidgetPrayerPage() {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [masjidInfo, setMasjidInfo] = useState(null);
  const [widgetToken, setWidgetToken] = useState(null);
  const [generatingToken, setGeneratingToken] = useState(false);
  
  // Daily Prayer Widget customization
  const [dailyTheme, setDailyTheme] = useState("light");
  const [dailyColor, setDailyColor] = useState("#bf9a2f");
  const [dailySize, setDailySize] = useState("medium");
  const [dailyHeight, setDailyHeight] = useState(400);
  const [copiedDaily, setCopiedDaily] = useState(false);

  // Monthly Calendar Widget customization
  const [monthlyTheme, setMonthlyTheme] = useState("light");
  const [monthlyColor, setMonthlyColor] = useState("#bf9a2f");
  const [monthlyWidth, setMonthlyWidth] = useState("max");
  const [monthlyHeight, setMonthlyHeight] = useState("max");
  const [monthlyCustomWidth, setMonthlyCustomWidth] = useState(1200);
  const [monthlyCustomHeight, setMonthlyCustomHeight] = useState(800);
  const [copiedMonthly, setCopiedMonthly] = useState(false);

  // Scroll refs for navigation
  const dailyRef = useRef(null);
  const monthlyRef = useRef(null);

  const DAILY_HEIGHT_PRESETS = {
    small: 250,
    medium: 400,
    large: 500,
  };

  useEffect(() => {
    const loadMasjidInfo = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const token = await auth.currentUser?.getIdToken();
        if (!token) return;

        const response = await fetch(`${API_BASE_URL}/v1/masjid/admin/info`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          setMasjidInfo(data);
        }
      } catch (err) {
        console.error("Error loading masjid info:", err);
      } finally {
        setLoading(false);
      }
    };

    loadMasjidInfo();
  }, [user]);

  useEffect(() => {
    if (masjidInfo?.masjid?.id && !widgetToken && !generatingToken) {
      generateWidgetToken();
    }
  }, [masjidInfo]);

  const generateWidgetToken = async () => {
    try {
      setGeneratingToken(true);
      if (!auth.currentUser) return null;

      const token = await auth.currentUser.getIdToken(true);
      const masjidId = masjidInfo?.masjid?.id || userProfile?.masjidId || userProfile?.activeMasjidId;

      if (!masjidId) return null;

      const response = await fetch(`${API_BASE_URL}/v1/widget-token/${masjidId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await response.json();

      if (data.ok) {
        setWidgetToken(data.token);
        return data.token;
      }
    } catch (err) {
      console.error("Error generating widget token:", err);
    } finally {
      setGeneratingToken(false);
    }
    return null;
  };

  // Daily widget helpers
  const normalizeDailyHeight = (val, fallback) => {
    const num = Number(val);
    if (Number.isFinite(num) && num > 0) {
      return Math.max(200, num);
    }
    return fallback;
  };

  const resolveDailyHeight = (sizeValue, customHeightValue) => {
    if (sizeValue === "custom") {
      return normalizeDailyHeight(customHeightValue, DAILY_HEIGHT_PRESETS.medium);
    }
    return DAILY_HEIGHT_PRESETS[sizeValue] || DAILY_HEIGHT_PRESETS.medium;
  };

  const getDailyWidgetUrl = () => {
    if (!widgetToken) return "";
    const colorParam = dailyColor.startsWith("#") ? dailyColor.substring(1) : dailyColor;
    const height = resolveDailyHeight(dailySize, dailyHeight);
    const heightQuery = height ? `&height=${height}` : "";
    
    return `${window.location.origin}/widget?token=${widgetToken}&theme=${dailyTheme}&color=${colorParam}&size=${dailySize}${heightQuery}`;
  };

  const getDailySizeStyles = () => {
    const height = resolveDailyHeight(dailySize, dailyHeight);
    const width = Math.max(220, Math.round((height * 9) / 16));

    return {
      maxWidth: `${width}px`,
      height: `${height}px`,
    };
  };

  const getDailyEmbedCode = (widgetUrl) => {
    const sizeStyles = getDailySizeStyles();
    return `<iframe src="${widgetUrl}" frameborder="0" scrolling="no" style="border: 1px solid #e5e7eb; border-radius: 12px; width: 100%; max-width: ${sizeStyles.maxWidth}; height: ${sizeStyles.height}; display: block; margin: 0 auto;"></iframe>`;
  };

  const handleDailySizeSelect = (size) => {
    setDailySize(size);
    if (size !== "custom") {
      setDailyHeight(resolveDailyHeight(size, dailyHeight));
    }
  };

  const copyDailyCode = async () => {
    try {
      const widgetUrl = getDailyWidgetUrl();
      if (!widgetUrl) return;

      if (dailySize === "custom") {
        const num = Number(dailyHeight);
        if (!Number.isFinite(num) || num < 200) {
          window.alert("Please enter a valid height (200px or greater) before copying.");
          return;
        }
      }

      const code = getDailyEmbedCode(widgetUrl);
      await navigator.clipboard.writeText(code);
      setCopiedDaily(true);
      setTimeout(() => setCopiedDaily(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Monthly widget helpers
  const normalizeMonthlyValue = (val, fallback) => {
    if (val === "max") return "max";
    const num = Number(val);
    if (Number.isFinite(num) && num > 0) {
      return Math.max(320, num);
    }
    return fallback;
  };

  const getMonthlyWidgetUrl = () => {
    if (!widgetToken) return "";
    const colorParam = monthlyColor.startsWith("#") ? monthlyColor.substring(1) : monthlyColor;
    const width = monthlyWidth === "max" ? "max" : normalizeMonthlyValue(monthlyCustomWidth, 1200);
    const height = monthlyHeight === "max" ? "max" : normalizeMonthlyValue(monthlyCustomHeight, 800);
    
    return `${window.location.origin}/widget-monthly?token=${widgetToken}&theme=${monthlyTheme}&color=${colorParam}&width=${width}&height=${height}`;
  };

  const getMonthlyPreviewDimensions = () => {
    const width = monthlyWidth === "max" ? "100%" : `${normalizeMonthlyValue(monthlyCustomWidth, 1200)}px`;
    const height = monthlyHeight === "max" ? "700px" : `${normalizeMonthlyValue(monthlyCustomHeight, 800)}px`;
    return { width, height };
  };

  const getMonthlyEmbedCode = (widgetUrl) => {
    const dims = getMonthlyPreviewDimensions();
    const widthStyle = monthlyWidth === "max" ? "width: 100%;" : `width: ${dims.width}; max-width: 100%;`;
    const heightStyle = monthlyHeight === "max" ? "height: 100vh; min-height: 600px;" : `height: ${dims.height};`;
    
    return `<style>@media(max-width:767px){.muslimplus-monthly-widget{max-height:750px!important;min-height:0!important}}</style>\n<iframe class="muslimplus-monthly-widget" src="${widgetUrl}" frameborder="0" scrolling="no" style="border: 1px solid #e5e7eb; border-radius: 12px; ${widthStyle} ${heightStyle} display: block; margin: 0 auto;"></iframe>`;
  };

  const copyMonthlyCode = async () => {
    try {
      const widgetUrl = getMonthlyWidgetUrl();
      if (!widgetUrl) return;

      if (monthlyWidth !== "max") {
        const num = Number(monthlyCustomWidth);
        if (!Number.isFinite(num) || num < 320) {
          window.alert("Please enter a valid width (320px or greater) before copying.");
          return;
        }
      }

      if (monthlyHeight !== "max") {
        const num = Number(monthlyCustomHeight);
        if (!Number.isFinite(num) || num < 400) {
          window.alert("Please enter a valid height (400px or greater) before copying.");
          return;
        }
      }

      const code = getMonthlyEmbedCode(widgetUrl);
      await navigator.clipboard.writeText(code);
      setCopiedMonthly(true);
      setTimeout(() => setCopiedMonthly(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex min-h-[70vh] flex-col items-center justify-center gap-6">
          <Loader2 className="h-16 w-16 animate-spin text-primary" />
          <p className="text-lg text-muted-foreground">Loading widget configuration...</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-6xl space-y-8 px-4 py-8">
        {/* Header with Back Button */}
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate("/widgets")}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/10">
              <Clock className="h-6 w-6 text-emerald-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Prayer Times Widgets</h1>
              <p className="text-sm text-muted-foreground">
                Choose between Daily or Monthly calendar views
              </p>
            </div>
          </div>
        </div>

        {/* Masjid Info Badge */}
        {masjidInfo?.masjid && (
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 rounded-full border bg-card px-4 py-2">
              <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-medium">{masjidInfo.masjid.name}</span>
            </div>
          </div>
        )}

        {/* Quick Navigation */}
        <div className="flex justify-center gap-4">
          <Button
            variant="outline"
            onClick={() => scrollToSection(dailyRef)}
            className="gap-2"
          >
            <Clock className="h-4 w-4" />
            Daily Prayer Times
          </Button>
          <Button
            variant="outline"
            onClick={() => scrollToSection(monthlyRef)}
            className="gap-2"
          >
            <CalendarDays className="h-4 w-4" />
            Monthly Calendar
          </Button>
        </div>

        {/* ==================== DAILY PRAYER TIMES WIDGET ==================== */}
        <div ref={dailyRef} className="scroll-mt-4">
          <div className="mb-6 flex items-center gap-3 border-b pb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10">
              <Clock className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Daily Prayer Times</h2>
              <p className="text-sm text-muted-foreground">Compact widget showing today's prayer times with day navigation</p>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Customization Panel */}
            <Card className="border-none shadow-lg">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-muted-foreground" />
                  <CardTitle>Customization</CardTitle>
                </div>
                <CardDescription>
                  Adjust the appearance to match your website
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Theme */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Theme</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={dailyTheme === "light" ? "default" : "outline"}
                      onClick={() => setDailyTheme("light")}
                      className="flex-1"
                    >
                      <Sun className="mr-2 h-4 w-4" />
                      Light
                    </Button>
                    <Button
                      variant={dailyTheme === "dark" ? "default" : "outline"}
                      onClick={() => setDailyTheme("dark")}
                      className="flex-1"
                    >
                      <Moon className="mr-2 h-4 w-4" />
                      Dark
                    </Button>
                  </div>
                </div>

                {/* Accent Color */}
                <div className="space-y-2">
                  <Label htmlFor="dailyColor" className="text-sm font-medium">
                    Accent Color
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="dailyColor"
                      type="color"
                      value={dailyColor}
                      onChange={(e) => setDailyColor(e.target.value)}
                      className="h-10 w-16 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={dailyColor}
                      onChange={(e) => setDailyColor(e.target.value)}
                      placeholder="#bf9a2f"
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Size */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Size</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant={dailySize === "small" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleDailySizeSelect("small")}
                    >
                      Small (250px)
                    </Button>
                    <Button
                      variant={dailySize === "medium" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleDailySizeSelect("medium")}
                    >
                      Medium (400px)
                    </Button>
                    <Button
                      variant={dailySize === "large" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleDailySizeSelect("large")}
                    >
                      Large (500px)
                    </Button>
                    <Button
                      variant={dailySize === "custom" ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleDailySizeSelect("custom")}
                    >
                      Custom
                    </Button>
                  </div>
                  {dailySize === "custom" && (
                    <div className="space-y-1 pt-2">
                      <Label className="text-xs text-muted-foreground" htmlFor="dailyCustomHeight">
                        Custom height (px)
                      </Label>
                      <Input
                        id="dailyCustomHeight"
                        type="number"
                        min="200"
                        value={dailyHeight}
                        onChange={(e) => setDailyHeight(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                  )}
                </div>

                {/* Embed Code */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Embed Code</Label>
                    <Button
                      size="sm"
                      onClick={copyDailyCode}
                      disabled={!widgetToken}
                      className="gap-2"
                    >
                      {copiedDaily ? (
                        <>
                          <Check className="h-4 w-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="rounded-lg bg-muted/50 border p-4 max-h-32 overflow-y-auto">
                    {!widgetToken ? (
                      <div className="flex items-center justify-center py-4">
                        <p className="text-sm text-muted-foreground">
                          Generating token...
                        </p>
                      </div>
                    ) : (
                      <code className="text-xs break-all whitespace-pre-wrap font-mono block">
                        {getDailyEmbedCode(getDailyWidgetUrl())}
                      </code>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Preview Panel */}
            <Card className="border-none shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="h-5 w-5 text-muted-foreground" />
                    <CardTitle>Live Preview</CardTitle>
                  </div>
                  {widgetToken && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const widgetUrl = getDailyWidgetUrl();
                        if (widgetUrl) {
                          window.open(widgetUrl, "_blank", "noopener,noreferrer");
                        }
                      }}
                      className="gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open
                    </Button>
                  )}
                </div>
                <CardDescription>
                  See how your widget will look on your website
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border bg-muted/30 overflow-hidden flex items-center justify-center p-4" style={{ minHeight: "500px" }}>
                  {widgetToken ? (
                    <iframe
                      src={getDailyWidgetUrl()}
                      style={{ 
                        border: "none",
                        width: getDailySizeStyles().maxWidth,
                        height: getDailySizeStyles().height,
                        maxWidth: "100%",
                        display: "block"
                      }}
                      title="Daily Prayer Widget Preview"
                    />
                  ) : (
                    <div className="flex h-full min-h-[500px] items-center justify-center">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">
                          Generating widget token...
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-4 text-center text-sm text-muted-foreground">
                  Preview size: {getDailySizeStyles().maxWidth} × {getDailySizeStyles().height}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Section */}
          <Card className="mt-6 border-none bg-muted/50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10">
                  <FileText className="h-5 w-5 text-emerald-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold">About this widget</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    The Daily Prayer Times widget displays your masjid's current prayer times with navigation to view upcoming days. 
                    It automatically updates with your latest prayer times and requires no maintenance after embedding. 
                    The widget shows Fajr, Sunrise, Dhuhr, Asr, Maghrib, and Isha times with a clean, modern design.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ==================== MONTHLY CALENDAR WIDGET ==================== */}
        <div ref={monthlyRef} className="scroll-mt-4 pt-8">
          <div className="mb-6 flex items-center gap-3 border-b pb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
              <CalendarDays className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Monthly Prayer Calendar</h2>
              <p className="text-sm text-muted-foreground">Horizontal table with Adhan/Iqama columns - ideal for TV displays</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Preview Panel - Full Width on Top */}
            <Card className="border-none shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="h-5 w-5 text-muted-foreground" />
                    <CardTitle>Live Preview</CardTitle>
                  </div>
                  {widgetToken && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const widgetUrl = getMonthlyWidgetUrl();
                        if (widgetUrl) {
                          window.open(widgetUrl, "_blank", "noopener,noreferrer");
                        }
                      }}
                      className="gap-2"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Open Full Screen
                    </Button>
                  )}
                </div>
                <CardDescription>
                  See how your widget will look on your website
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border bg-muted/30 overflow-hidden flex items-center justify-center p-4" style={{ minHeight: "500px" }}>
                  {widgetToken ? (
                    <iframe
                      src={getMonthlyWidgetUrl()}
                      style={{ 
                        border: "none",
                        width: monthlyWidth === "max" ? "100%" : `${Math.min(normalizeMonthlyValue(monthlyCustomWidth, 1200), 900)}px`,
                        height: monthlyHeight === "max" ? "500px" : `${Math.min(normalizeMonthlyValue(monthlyCustomHeight, 800), 600)}px`,
                        maxWidth: "100%",
                        display: "block"
                      }}
                      title="Monthly Calendar Widget Preview"
                    />
                  ) : (
                    <div className="flex h-full min-h-[500px] items-center justify-center">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        <p className="text-sm text-muted-foreground">
                          Generating widget token...
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-4 text-center text-sm text-muted-foreground">
                  Preview: {monthlyWidth === "max" ? "100%" : `${normalizeMonthlyValue(monthlyCustomWidth, 1200)}px`} × {monthlyHeight === "max" ? "Fill container" : `${normalizeMonthlyValue(monthlyCustomHeight, 800)}px`}
                </div>
              </CardContent>
            </Card>

            {/* Customization Panel */}
            <Card className="border-none shadow-lg">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Settings className="h-5 w-5 text-muted-foreground" />
                  <CardTitle>Customization</CardTitle>
                </div>
                <CardDescription>
                  Displays horizontal table showing both Adhan and Iqama times for all prayers
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Theme */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Theme</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={monthlyTheme === "light" ? "default" : "outline"}
                      onClick={() => setMonthlyTheme("light")}
                      className="flex-1"
                    >
                      <Sun className="mr-2 h-4 w-4" />
                      Light
                    </Button>
                    <Button
                      variant={monthlyTheme === "dark" ? "default" : "outline"}
                      onClick={() => setMonthlyTheme("dark")}
                      className="flex-1"
                    >
                      <Moon className="mr-2 h-4 w-4" />
                      Dark
                    </Button>
                  </div>
                </div>

                {/* Accent Color */}
                <div className="space-y-2">
                  <Label htmlFor="monthlyColor" className="text-sm font-medium">
                    Accent Color
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="monthlyColor"
                      type="color"
                      value={monthlyColor}
                      onChange={(e) => setMonthlyColor(e.target.value)}
                      className="h-10 w-16 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={monthlyColor}
                      onChange={(e) => setMonthlyColor(e.target.value)}
                      placeholder="#bf9a2f"
                      className="flex-1 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Size */}
                <div className="space-y-4">
                  <Label className="text-sm font-medium">Dimensions</Label>
                  <p className="text-xs text-muted-foreground -mt-2">
                    Use "Max" for full-width displays like TVs or large monitors
                  </p>
                  
                  {/* Width */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Width</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={monthlyWidth === "max" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setMonthlyWidth("max")}
                      >
                        Max (Fill Container)
                      </Button>
                      <Button
                        variant={monthlyWidth !== "max" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setMonthlyWidth("custom")}
                      >
                        Custom
                      </Button>
                    </div>
                    {monthlyWidth !== "max" && (
                      <Input
                        type="number"
                        min="320"
                        value={monthlyCustomWidth}
                        onChange={(e) => setMonthlyCustomWidth(e.target.value)}
                        className="text-sm"
                        placeholder="Width in pixels (min 320)"
                      />
                    )}
                  </div>

                  {/* Height */}
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Height</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={monthlyHeight === "max" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setMonthlyHeight("max")}
                      >
                        Max (Fill Container)
                      </Button>
                      <Button
                        variant={monthlyHeight !== "max" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setMonthlyHeight("custom")}
                      >
                        Custom
                      </Button>
                    </div>
                    {monthlyHeight !== "max" && (
                      <Input
                        type="number"
                        min="400"
                        value={monthlyCustomHeight}
                        onChange={(e) => setMonthlyCustomHeight(e.target.value)}
                        className="text-sm"
                        placeholder="Height in pixels (min 400)"
                      />
                    )}
                  </div>
                </div>

                {/* Embed Code */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Embed Code</Label>
                    <Button
                      size="sm"
                      onClick={copyMonthlyCode}
                      disabled={!widgetToken}
                      className="gap-2"
                    >
                      {copiedMonthly ? (
                        <>
                          <Check className="h-4 w-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                  <div className="rounded-lg bg-muted/50 border p-4 max-h-32 overflow-y-auto">
                    {!widgetToken ? (
                      <div className="flex items-center justify-center py-4">
                        <p className="text-sm text-muted-foreground">
                          Generating token...
                        </p>
                      </div>
                    ) : (
                      <code className="text-xs break-all whitespace-pre-wrap font-mono block">
                        {getMonthlyEmbedCode(getMonthlyWidgetUrl())}
                      </code>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Section */}
          <Card className="mt-6 border-none bg-muted/50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
                  <FileText className="h-5 w-5 text-blue-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold">About this widget</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    The Monthly Prayer Calendar widget displays a full month view of prayer times in a horizontal table format, 
                    showing both Adhan and Iqama times for each prayer. This layout is ideal for TV displays, digital signage, 
                    and large monitors. On mobile devices, it switches to a compact day-selector view. 
                    Today's date is highlighted for quick reference, and users can navigate between months.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
